// We need to register it again because of Vue instance instantiation issues

import type { EventLocationPreference } from '~/components/common/card/TeameventRequestCard.vue'
import { sendViewWishlist } from '~/composables/useGtmEvents'
import {
  isMobileDrawerQueryEnabled,
  type DrawerQueryValue,
  useDrawerQueryNavigation,
} from '~/composables/useDrawerQueryNavigation'

export interface UiState {
  isCartSidebarOpen: boolean
  isDateRequestSidebarOpen: boolean
  isWaitlistSidebarOpen: boolean
  isNavigationSidebarOpen: boolean
  isFederatedSearchOpen: boolean
  eventRequestOpenedPage: string | undefined
  dateRequestSidebarRequestLocation: string | undefined
  dateRequestSidebarRequestPreferredLocation:
    | EventLocationPreference
    | undefined
  /** Buy-box party size synced after the user changes quantity on an event page. */
  dateRequestSuggestedTicketCount: number | undefined
}

const setBodyScrollLocked = (locked: boolean) => {
  if (typeof document === 'undefined' || !document.body) return

  document.body.classList.remove('prevent-scroll')
  if (locked) {
    document.body.classList.add('prevent-scroll')
  }
}

const SIDEBAR_DRAWER_QUERY_BY_KEY: Record<string, DrawerQueryValue> = {
  activeOffers: 'active-offers',
  wishlist: 'wishlist',
}

export const useUiState = () => {
  if (!import.meta.client) {
    return {
      isCartSidebarOpen: ref(false),
      isDateRequestSidebarOpen: ref(false),
      eventRequestOpenedPage: ref(undefined),
      isWaitlistSidebarOpen: ref(false),
      isNavigationSidebarOpen: ref(false),
      isFederatedSearchOpen: ref(false),
      dateRequestSidebarRequestLocation: '',
      dateRequestSidebarRequestPreferredLocation: ref<
        EventLocationPreference | undefined
      >(undefined),
      dateRequestSuggestedTicketCount: ref<number | undefined>(undefined),
      setDateRequestSuggestedTicketCount: () => {},
      toggleCartSidebar: () => {},
      toggleDateRequestSidebar: () => {},
      toggleWaitlistSidebar: () => {},
      toggleNavigationSidebar: () => {},
      toggleFederatedSearchOpen: () => {},
      closeCartSidebar: () => {},
      closeDateRequestSidebar: () => {},
      closeWaitlistSidebar: () => {},
    }
  }

  const state = useState<UiState>('uiState', () => ({
    isCartSidebarOpen: false,
    isDateRequestSidebarOpen: false,
    eventRequestOpenedPage: undefined,
    isWaitlistSidebarOpen: false,
    isNavigationSidebarOpen: false,
    isFederatedSearchOpen: false,
    dateRequestSidebarRequestLocation: '',
    dateRequestSidebarRequestPreferredLocation: undefined,
    dateRequestSuggestedTicketCount: undefined,
  }))

  const usesDrawerQuery = isMobileDrawerQueryEnabled()
  const {
    currentDrawer,
    pushDrawerQuery,
    popDrawerQuery,
    clearDrawerQueryFromUrl,
  } = useDrawerQueryNavigation()

  const isCartSidebarOpen = computed(() =>
    usesDrawerQuery
      ? currentDrawer.value === 'cart'
      : state.value.isCartSidebarOpen
  )

  const closeCartSidebar = () => {
    if (usesDrawerQuery) {
      if (currentDrawer.value === 'cart') {
        clearDrawerQueryFromUrl()
      }
      return
    }

    if (!state.value.isCartSidebarOpen) return

    state.value.isCartSidebarOpen = false
    setBodyScrollLocked(false)
  }

  const toggleCartSidebar = (send_to_gtm: boolean = false) => {
    if (usesDrawerQuery) {
      if (currentDrawer.value === 'cart') {
        popDrawerQuery()
        return
      }

      pushDrawerQuery('cart')

      if (send_to_gtm) {
        sendViewCart()
      }
      return
    }

    if (state.value.isCartSidebarOpen) {
      closeCartSidebar()
      return
    }

    state.value.isCartSidebarOpen = true
    setBodyScrollLocked(true)

    if (send_to_gtm) {
      sendViewCart()
    }
  }

  const isDateRequestSidebarOpen = computed(() =>
    usesDrawerQuery
      ? currentDrawer.value === 'date-request'
      : state.value.isDateRequestSidebarOpen
  )

  const closeDateRequestSidebar = () => {
    if (usesDrawerQuery) {
      if (currentDrawer.value === 'date-request') {
        clearDrawerQueryFromUrl()
      }
      return
    }

    if (!state.value.isDateRequestSidebarOpen) return

    state.value.isDateRequestSidebarOpen = false
    setBodyScrollLocked(false)
  }

  const toggleDateRequestSidebar = (
    openedPage: string | undefined = undefined,
    requestLocation: string | undefined = undefined,
    wantedLocation: EventLocationPreference | undefined = undefined,
    suggestedTicketCount: number | undefined = undefined
  ) => {
    if (usesDrawerQuery) {
      if (currentDrawer.value === 'date-request') {
        popDrawerQuery()
        return
      }

      state.value.eventRequestOpenedPage = openedPage
      state.value.dateRequestSidebarRequestLocation = requestLocation
      state.value.dateRequestSidebarRequestPreferredLocation = wantedLocation

      if (suggestedTicketCount !== undefined) {
        state.value.dateRequestSuggestedTicketCount = suggestedTicketCount
      }

      pushDrawerQuery('date-request')
      sendDateRequestSidebarView(requestLocation || '')
      return
    }

    if (state.value.isDateRequestSidebarOpen) {
      closeDateRequestSidebar()
      return
    }

    state.value.isDateRequestSidebarOpen = true
    state.value.eventRequestOpenedPage = openedPage
    state.value.dateRequestSidebarRequestLocation = requestLocation
    state.value.dateRequestSidebarRequestPreferredLocation = wantedLocation

    if (suggestedTicketCount !== undefined) {
      state.value.dateRequestSuggestedTicketCount = suggestedTicketCount
    }

    setBodyScrollLocked(true)
    sendDateRequestSidebarView(requestLocation || '')
  }

  const eventRequestOpenedPage = computed(
    () => state.value.eventRequestOpenedPage
  )

  const isWaitlistSidebarOpen = computed(() =>
    usesDrawerQuery
      ? currentDrawer.value === 'waitlist'
      : state.value.isWaitlistSidebarOpen
  )

  const closeWaitlistSidebar = () => {
    if (usesDrawerQuery) {
      if (currentDrawer.value === 'waitlist') {
        clearDrawerQueryFromUrl()
      }
      return
    }

    if (!state.value.isWaitlistSidebarOpen) return

    state.value.isWaitlistSidebarOpen = false
    setBodyScrollLocked(false)
  }

  const toggleWaitlistSidebar = () => {
    if (usesDrawerQuery) {
      if (currentDrawer.value === 'waitlist') {
        popDrawerQuery()
        return
      }

      pushDrawerQuery('waitlist')
      sendWaitlistSidebarView()
      return
    }

    if (state.value.isWaitlistSidebarOpen) {
      closeWaitlistSidebar()
      return
    }

    state.value.isWaitlistSidebarOpen = true
    setBodyScrollLocked(true)
    sendWaitlistSidebarView()
  }

  const isNavigationSidebarOpen = computed(
    () => state.value.isNavigationSidebarOpen
  )
  const toggleNavigationSidebar = () => {
    state.value.isNavigationSidebarOpen = !state.value.isNavigationSidebarOpen
  }

  const isFederatedSearchOpen = computed(
    () => state.value.isFederatedSearchOpen
  )
  const toggleFederatedSearchOpen = () => {
    state.value.isFederatedSearchOpen = !state.value.isFederatedSearchOpen
  }

  const dateRequestSidebarRequestLocation = computed(
    () => state.value.dateRequestSidebarRequestLocation
  )
  const dateRequestSidebarRequestPreferredLocation = computed(
    () => state.value.dateRequestSidebarRequestPreferredLocation
  )
  const dateRequestSuggestedTicketCount = computed(
    () => state.value.dateRequestSuggestedTicketCount
  )
  const setDateRequestSuggestedTicketCount = (
    ticketCount: number | undefined
  ) => {
    state.value.dateRequestSuggestedTicketCount = ticketCount
  }

  if (usesDrawerQuery) {
    watch(
      currentDrawer,
      (drawer) => {
        setBodyScrollLocked(!!drawer)
      },
      { immediate: true }
    )
  }

  return {
    isCartSidebarOpen,
    isDateRequestSidebarOpen,
    eventRequestOpenedPage,
    isWaitlistSidebarOpen,
    isNavigationSidebarOpen,
    isFederatedSearchOpen,
    dateRequestSidebarRequestLocation,
    dateRequestSidebarRequestPreferredLocation,
    dateRequestSuggestedTicketCount,
    setDateRequestSuggestedTicketCount,
    toggleCartSidebar,
    toggleDateRequestSidebar,
    toggleWaitlistSidebar,
    toggleNavigationSidebar,
    toggleFederatedSearchOpen,
    closeCartSidebar,
    closeDateRequestSidebar,
    closeWaitlistSidebar,
  }
}

export const useSidebarUiState = (
  key: string,
  { gtmCb }: { gtmCb?: (...args: any) => any } = {}
) => {
  if (!import.meta.client) {
    return {
      isOpen: false,
      close: () => {},
      toggle: () => {},
    }
  }

  const localIsOpen = useState(`singleUiState-${key}`, () => false)
  const drawerQueryValue = SIDEBAR_DRAWER_QUERY_BY_KEY[key]
  const usesDrawerQuery = isMobileDrawerQueryEnabled() && !!drawerQueryValue
  const {
    currentDrawer,
    pushDrawerQuery,
    popDrawerQuery,
    clearDrawerQueryFromUrl,
  } = useDrawerQueryNavigation()

  const isOpen = computed(() =>
    usesDrawerQuery
      ? currentDrawer.value === drawerQueryValue
      : localIsOpen.value
  )

  const close = () => {
    if (usesDrawerQuery) {
      if (currentDrawer.value === drawerQueryValue) {
        clearDrawerQueryFromUrl()
      }
      return
    }

    if (!localIsOpen.value) return

    localIsOpen.value = false
    setBodyScrollLocked(false)
  }

  const toggle = (send_to_gtm: boolean = false) => {
    if (usesDrawerQuery && drawerQueryValue) {
      if (currentDrawer.value === drawerQueryValue) {
        popDrawerQuery()
        return
      }

      pushDrawerQuery(drawerQueryValue)

      if (send_to_gtm && gtmCb) {
        gtmCb()
      }
      return
    }

    if (localIsOpen.value) {
      close()
      return
    }

    localIsOpen.value = true
    setBodyScrollLocked(true)

    if (send_to_gtm && gtmCb) {
      gtmCb()
    }
  }

  return {
    isOpen,
    toggle,
    close,
  }
}

export const useWishlistSidebarUiState = () => {
  const { wishlist } = useWishlist()
  return useSidebarUiState('wishlist', {
    gtmCb: () => sendViewWishlist(wishlist.value),
  })
}

export const useActiveOffersSidebarUiState = () => {
  return useSidebarUiState('activeOffers')
}
